import { ref, h, createApp } from 'vue';
import Toast from '@/components/ui/Toast.vue';

const toasts = ref([]);
let toastId = 0;
let toastContainer = null;

const ensureContainer = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return false;

  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.className = 'toast-container';
    document.body.appendChild(toastContainer);
  }

  return true;
};

const showToast = (message, options = {}) => {
  if (!ensureContainer()) return { dismiss: () => {} };

  const id = toastId++;
  const { type = 'info', duration = 5000 } = options;

  const toast = {
    id,
    message,
    type,
    duration,
    dismiss: null,
  };

  const dismiss = () => {
    const index = toasts.value.findIndex(t => t.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  };

  toast.dismiss = dismiss;
  toasts.value = [...toasts.value, toast];

  // Auto-dismiss if duration is set
  if (duration > 0) {
    setTimeout(dismiss, duration);
  }

  return { dismiss };
};

// Helper methods
const success = (message, options = {}) => {
  return showToast(message, { ...options, type: 'success' });
};

const error = (message, options = {}) => {
  return showToast(message, { ...options, type: 'error' });
};

const info = (message, options = {}) => {
  return showToast(message, { ...options, type: 'info' });
};

const warning = (message, options = {}) => {
  return showToast(message, { ...options, type: 'warning' });
};

// Toast container component
const ToastContainer = {
  setup() {
    return { toasts };
  },
  render() {
    return h(
      'div',
      { class: 'toast-container' },
      this.toasts.map(toast =>
        h(Toast, {
          key: toast.id,
          message: toast.message,
          type: toast.type,
          duration: toast.duration,
          onDismiss: toast.dismiss,
        })
      )
    );
  },
};

// Create the toast instance with all methods
const createToastInstance = () => ({
  show: showToast,
  success,
  error,
  info,
  warning,
});

// Create a single instance
const toastInstance = createToastInstance();

export default {
  install(app) {
    // Initialize the toast container
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      if (ensureContainer()) {
        const toastApp = createApp(ToastContainer);
        toastApp.mount(toastContainer);
      }
    }

    // Make toast available globally as $toast
    app.config.globalProperties.$toast = toastInstance;

    // Also provide it for Composition API
    app.provide('toast', toastInstance);
  }
};

// Export the toast functions for direct import
export const toast = toastInstance;
